import { memo } from 'react';

import { useTranslation } from '@mint-lib/i18n';
import { useFlags } from '@mint-lib/routing-context';

import { Autocomplete, TextField } from '@myn/mui';

import {
  filterSoftDeletedOptions,
  getOptionDisabled,
  getOptionLabel,
  isOptionEqualToValue,
} from '../../../../../../utils/autocomplete.js';
import { BuilderTableColumn, BuilderTableRow } from '../../../../types.js';
import FormattedField from '../FormattedField/FormattedField.jsx';
import useSelectableValueField from '../hooks/useSelectableValueField.js';
import TableEditCell from '../TableEditCell/TableEditCell.jsx';
import AutocompleteWrapper from '../wrappers/AutocompleteWrapper.jsx';

export type Props = {
  value: string | null;
  column: BuilderTableColumn;
  row: BuilderTableRow;
  cellColor: string;
};

const SelectTextValue = memo(({ value, column, row, cellColor }: Props) => {
  const { t } = useTranslation('@myn/planning');
  const ff = useFlags('@mint/arm', ['mdmColumnsForBuyingPlan']);

  const { scale } = row.original;

  const {
    selectedOption,
    autocompleteInputValue,
    isFieldFocused,
    isReadOnlyField,
    isResetBtnShown,
    handleOpenMenu,
    handleCloseMenu,
    handleChangeValue,
    handleResetValue,
    handleAutocompleteInputValueChange,
  } = useSelectableValueField({
    value,
    column,
    row,
  });

  if (isReadOnlyField) {
    return (
      <FormattedField
        scale={scale}
        value={selectedOption.displayName}
        variant="text"
        color={cellColor}
      />
    );
  }

  if (!isFieldFocused) {
    return (
      <TableEditCell
        scale={scale}
        isDeleteButtonShown={isResetBtnShown}
        deleteValueFunc={handleResetValue}
        showEditModeFunc={handleOpenMenu}
        startMeasurePerformance={column.startMeasurePerformance}
        value={selectedOption.displayName}
        variant="text"
      />
    );
  }

  return (
    <AutocompleteWrapper $scale={scale}>
      <Autocomplete
        size="medium"
        options={column.options}
        renderInput={(params) => (
          <TextField {...params} autoFocus placeholder={t('Search')} />
        )}
        getOptionLabel={getOptionLabel}
        isOptionEqualToValue={isOptionEqualToValue}
        value={selectedOption}
        onChange={handleChangeValue}
        openOnFocus
        onClose={handleCloseMenu}
        disableClearable={!autocompleteInputValue}
        inputValue={autocompleteInputValue}
        onInputChange={handleAutocompleteInputValueChange}
        filterOptions={filterSoftDeletedOptions}
        getOptionDisabled={
          ff.mdmColumnsForBuyingPlan.enabled ? getOptionDisabled : undefined
        }
      />
    </AutocompleteWrapper>
  );
});

export default SelectTextValue;
